import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { OntologyClass, PropertyDefinition } from '@/types/ontology'
import { cn } from '@/lib/utils'
import { ArrowRight, Link2, Type } from 'lucide-react'
import { getLocalName } from '@/lib/ontology/namespaces'

interface ClassDetailProps {
  ontologyClass: OntologyClass
}

const domainColors: Record<string, string> = {
  core: 'border-l-amber-500',
  market: 'border-l-market',
  business: 'border-l-business',
  solution: 'border-l-solution',
}

function PropertyRow({ property }: { property: PropertyDefinition }) {
  const isObject = property.type === 'object'

  return (
    <div className="flex items-start gap-3 py-2 border-b border-border last:border-0">
      <div className="flex-shrink-0 mt-0.5">
        {isObject ? (
          <Link2 className="w-4 h-4 text-business" />
        ) : (
          <Type className="w-4 h-4 text-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="text-sm font-medium text-text">{property.localName}</code>
          <Badge variant={isObject ? 'business' : 'outline'} className="text-[10px]">
            {property.type}
          </Badge>
        </div>
        {property.description && (
          <p className="text-xs text-text-muted mt-0.5">{property.description}</p>
        )}
        <div className="flex items-center gap-1 mt-1 text-xs text-text-muted">
          <span className="font-mono">{getLocalName(property.domain)}</span>
          <ArrowRight className="w-3 h-3" />
          <span className="font-mono">{getLocalName(property.range)}</span>
        </div>
      </div>
    </div>
  )
}

export function ClassDetail({ ontologyClass }: ClassDetailProps) {
  const objectProperties = ontologyClass.properties.filter((p) => p.type === 'object')
  const dataProperties = ontologyClass.properties.filter((p) => p.type === 'data')

  return (
    <Card className={cn('border-l-4', domainColors[ontologyClass.domain])}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="font-mono text-xl">{ontologyClass.localName}</CardTitle>
            <CardDescription className="font-mono text-xs mt-1 break-all">
              {ontologyClass.uri}
            </CardDescription>
          </div>
          <Badge variant={ontologyClass.domain}>
            {ontologyClass.domain}
          </Badge>
        </div>
        {ontologyClass.description && (
          <p className="text-sm text-text-muted mt-2">{ontologyClass.description}</p>
        )}
        {ontologyClass.subClassOf && (
          <div className="flex items-center gap-2 mt-2 text-xs text-text-muted">
            <span>Subclass of:</span>
            <code className="bg-background px-1.5 py-0.5 rounded">
              {getLocalName(ontologyClass.subClassOf)}
            </code>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Object Properties */}
        {objectProperties.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text mb-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-business" />
              Object Properties
              <span className="text-xs text-text-muted">({objectProperties.length})</span>
            </h4>
            <div className="bg-background rounded-lg p-3">
              {objectProperties.map((prop) => (
                <PropertyRow key={prop.uri} property={prop} />
              ))}
            </div>
          </div>
        )}

        {/* Data Properties */}
        {dataProperties.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text mb-2 flex items-center gap-2">
              <Type className="w-4 h-4 text-text-muted" />
              Data Properties
              <span className="text-xs text-text-muted">({dataProperties.length})</span>
            </h4>
            <div className="bg-background rounded-lg p-3">
              {dataProperties.map((prop) => (
                <PropertyRow key={prop.uri} property={prop} />
              ))}
            </div>
          </div>
        )}

        {/* No Properties */}
        {ontologyClass.properties.length === 0 && (
          <p className="text-sm text-text-muted italic">No properties defined for this class.</p>
        )}
      </CardContent>
    </Card>
  )
}
